import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Search, MapPin, Clock, IndianRupee, Filter, Star, MessageSquare } from 'lucide-react';

export default function BrowseProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    skills: '',
    availability: '',
    min_experience: '',
    max_rate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.skills) params.append('skills', filters.skills);
      if (filters.availability) params.append('availability', filters.availability);
      if (filters.min_experience) params.append('min_experience', filters.min_experience);
      if (filters.max_rate) params.append('max_rate', filters.max_rate);
      
      const response = await api.get(`/profiles?${params.toString()}`);
      setProfiles(response.data.profiles || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchProfiles();
  }, []);
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfiles();
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const applyFilters = () => {
    fetchProfiles();
    setShowFilters(false);
  };
  
  const clearFilters = () => {
    setFilters({
      skills: '',
      availability: '',
      min_experience: '',
      max_rate: ''
    });
    setSearch('');
  };
  
  return (
    <DashboardLayout title="Browse Researchers">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
            <input
              type="text"
              data-testid="search-input"
              placeholder="Search researchers by skills, bio, or headline..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-neutral-200 bg-white focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none"
            />
          </div>
          <button
            type="button"
            data-testid="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 border ${showFilters ? 'border-[#0D7377] bg-[#E6F3F4]' : 'border-neutral-200 bg-white'} flex items-center gap-2 transition-colors`}
          >
            <Filter size={20} />
            <span className="hidden sm:inline">Filters</span>
          </button>
          <button
            type="submit"
            data-testid="search-submit"
            className="btn-client px-6 py-3"
          >
            Search
          </button>
        </div>
      </form>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-neutral-200 p-6 mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ fontFamily: 'Outfit' }}>Filters</h3>
            <button onClick={clearFilters} className="text-sm text-neutral-500 hover:text-neutral-700">
              Clear all
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Skills (comma separated)</label>
              <input
                type="text"
                data-testid="filter-skills"
                placeholder="e.g., Python, SPSS"
                value={filters.skills}
                onChange={(e) => handleFilterChange('skills', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Availability</label>
              <select
                data-testid="filter-availability"
                value={filters.availability}
                onChange={(e) => handleFilterChange('availability', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 bg-white"
              >
                <option value="">All</option>
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="not_available">Not Available</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Min Experience (years)</label>
              <input
                type="number"
                data-testid="filter-experience"
                placeholder="e.g., 3"
                value={filters.min_experience}
                onChange={(e) => handleFilterChange('min_experience', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Max Rate (₹/hr)</label>
              <input
                type="number"
                data-testid="filter-rate"
                placeholder="₹"
                value={filters.max_rate}
                onChange={(e) => handleFilterChange('max_rate', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              data-testid="apply-filters"
              onClick={applyFilters}
              className="btn-client px-6 py-2"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
      
      {/* Results */}
      <div className="text-sm text-neutral-500 mb-4">
        {total} researchers found
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D7377]"></div>
        </div>
      ) : profiles.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <p className="text-neutral-500 mb-2">No researchers found</p>
          <p className="text-sm text-neutral-400">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {profiles.map((profile) => (
            <div
              key={profile.user_id}
              data-testid={`profile-card-${profile.user_id}`}
              className="bg-white border border-neutral-200 p-6 card-client hover-lift"
            >
              <div className="flex items-start gap-4">
                {profile.user?.picture ? (
                  <img 
                    src={profile.user.picture} 
                    alt={profile.user.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#E6F3F4] flex items-center justify-center text-[#0D7377] font-bold text-xl">
                    {profile.user?.name?.[0]?.toUpperCase()}
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <Link to={`/profile/${profile.user_id}`}>
                    <h3 className="font-semibold text-lg text-neutral-900 hover:text-[#0D7377] transition-colors">
                      {profile.user?.name}
                    </h3>
                  </Link>
                  {profile.headline && (
                    <p className="text-sm text-neutral-600 mb-2">{profile.headline}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-3 text-sm text-neutral-500 mb-3">
                    {profile.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {profile.location}
                      </span>
                    )}
                    {profile.experience_years && (
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {profile.experience_years} yrs exp
                      </span>
                    )}
                    {profile.hourly_rate && (
                      <span className="flex items-center gap-1">
                        <IndianRupee size={14} />
                        ₹{profile.hourly_rate}/hr
                      </span>
                    )}
                  </div>
                  
                  {profile.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.slice(0, 4).map((skill, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-sm"
                        >
                          {skill}
                        </span>
                      ))}
                      {profile.skills.length > 4 && (
                        <span className="text-xs px-2 py-1 text-neutral-500">
                          +{profile.skills.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-xs px-2 py-1 rounded-sm font-medium ${
                    profile.availability === 'available'
                      ? 'bg-green-100 text-green-700'
                      : profile.availability === 'busy'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-neutral-100 text-neutral-500'
                  }`}>
                    {profile.availability?.replace('_', ' ') || 'Unknown'}
                  </span>
                  
                  <Link
                    to={`/profile/${profile.user_id}`}
                    data-testid={`view-profile-${profile.user_id}`}
                    className="text-sm text-[#0D7377] font-medium hover:underline"
                  >
                    View Profile
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
